/**
 * Phone number utilities for normalization, formatting, and validation.
 * Phone numbers are stored as digits only (e.g., "4165551234") and
 * formatted for display (e.g., "(416) 555-1234").
 */

/**
 * Normalize a phone number to digits only.
 * Strips all non-digit characters.
 *
 * @example
 * normalizePhone("(416) 555-1234") // "4165551234"
 * normalizePhone("416.555.1234")   // "4165551234"
 * normalizePhone("+1 416-555-1234") // "14165551234"
 */
export function normalizePhone(phone: string | null | undefined): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Format a normalized phone number for display.
 * Supports 10-digit (North American) and 11-digit (with country code) numbers.
 *
 * @example
 * formatPhone("4165551234")   // "(416) 555-1234"
 * formatPhone("14165551234")  // "+1 (416) 555-1234"
 * formatPhone("5551234")      // "555-1234" (7 digits)
 * formatPhone("")             // ""
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';

  const digits = normalizePhone(phone);

  // 10-digit North American format
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // 11-digit with country code (1 + 10)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // 7-digit local format
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  // For other lengths, return as-is with basic grouping
  if (digits.length > 10) {
    // International: +XX (XXX) XXX-XXXX or similar
    return phone;
  }

  // Return digits as-is if we can't format
  return digits || phone;
}

/**
 * Validate a phone number.
 * Must have at least 10 digits for North American numbers.
 *
 * @example
 * isValidPhone("4165551234")      // true
 * isValidPhone("(416) 555-1234")  // true
 * isValidPhone("555-1234")        // false (only 7 digits)
 * isValidPhone("")                // false
 */
export function isValidPhone(phone: string | null | undefined): boolean {
  if (!phone) return false;
  const digits = normalizePhone(phone);
  return digits.length >= 10;
}

/**
 * Format phone for input display while typing.
 * Adds formatting as the user types without blocking input.
 *
 * @example
 * formatPhoneInput("416")       // "416"
 * formatPhoneInput("4165")      // "(416) 5"
 * formatPhoneInput("4165551234") // "(416) 555-1234"
 */
export function formatPhoneInput(phone: string): string {
  const digits = normalizePhone(phone);

  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // For 11+ digits, assume country code
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return digits;
}

/**
 * Get phone type label for display.
 */
export function getPhoneTypeLabel(
  phoneType: 'mobile' | 'home' | 'work' | 'other'
): string {
  const labels: Record<string, string> = {
    mobile: 'Mobile',
    home: 'Home',
    work: 'Work',
    other: 'Other',
  };
  return labels[phoneType] || phoneType;
}
